import React, { useEffect, useState } from 'react';
import { BubbleAlert, BubbleButton, BubbleOverlay, BubbleSelect, BubbleTable } from '../BubbleUI';
import {
  BubbleTableFieldBehavior,
  BubbleTableFields,
  BubbleTableTemplate,
} from '../BubbleUI/BubbleTable';
import { FormGenericContainer } from '../FormGenericContainer';
import { Template } from '@go-shared/types/template';
import { TransferForm } from './TemplateTransferForm';
import TmplTnsfStatusFooter from './TmplTnsfStatusFooter';
import { getPropertyFeedBackgrounds } from '@go-client/lib/api/property';

interface TmplTransferImgPageProps {
  transferForm: TransferForm;
  isBusy: boolean;
  template: Template;
  changeTransferForm: <T extends keyof TransferForm>(key: T, val: TransferForm[T]) => void;
  onGoBack?: () => void;
  onFinishTransferTemplates?: () => void;
  onClose?: () => void;
}

export function TmplTransferImgPage(props: TmplTransferImgPageProps) {
  const { template, transferForm, changeTransferForm } = props;
  const [sourcePageOptions, setSourcePageOptions] = useState<string[]>([]);

  useEffect(() => {
    fetchData().catch(console.error);
  }, []);

  async function fetchData() {
    if (!transferForm.source) {
      console.warn('No source property set');
      return;
    }

    const options = await getPropertyFeedBackgrounds(transferForm.source._id);
    setSourcePageOptions(options);

    const allPages = (props.template.feeds ?? []).flatMap(feed => feed.pages ?? []);

    if (options.length > 0) {
      const prefilled: Record<string, string> = {};
      allPages.forEach((page, index) => {
        prefilled[page._id] = options[index] ?? options[0];
      });

      changeTransferForm('bgImagesDict', prefilled);
    }
  }

  return (
    <div className="tflf-4">
      <FormGenericContainer
        footerClassName="flex-wrap"
        footer={() => (
          <>
            <TmplTnsfStatusFooter {...props} className="text-slate-dark mr-auto" />
            <BubbleButton
              className="ml-auto"
              variant="navy"
              inverse
              onClick={props.onGoBack}
              disabled={props.isBusy}
            >
              Go Back
            </BubbleButton>
            <BubbleOverlay loading={props.isBusy}>
              <BubbleButton variant="navy" onClick={props.onFinishTransferTemplates}>
                Finish: Transfer Template
              </BubbleButton>
            </BubbleOverlay>
          </>
        )}
      >
        <div className="flex items-start mb-5">
          <div>
            <h1 className="text-lg text-slate-dark font-semibold">Transfer Template</h1>
            <h2 className="text-2xl text-navy">5. Confirm Feed Images</h2>
          </div>
          <BubbleButton
            disabled={props.isBusy}
            onClick={props.onClose}
            className="ml-auto"
            variant="navy"
            inverse
            icon="xmark-large"
            iconSize="xl"
          />
        </div>

        <BubbleAlert variant="banana" className="mb-6">
          Select the image to use for each target page.
        </BubbleAlert>

        {sourcePageOptions != undefined ? (
          <div>
            {(template.feeds ?? []).map((feed, feedIndex) => {
              const tableItems = feed.pages.map((page, pageIndex) => ({
                pageId: page._id,
                pageLabel: `Page ${pageIndex + 1}`,
                _data: page,
              }));

              const tableFields: BubbleTableFields<(typeof tableItems)[number]>[] = [
                { key: 'pageLabel', label: 'Target Page' },
                {
                  key: 'selectedSrc',
                  label: 'Image To Use',
                  behavior: BubbleTableFieldBehavior.Condense,
                },
              ];

              const tableTemplates: BubbleTableTemplate<(typeof tableItems)[number]>[] = [
                {
                  key: 'pageLabel',
                  render: row => (
                    <div>
                      <div>{row.pageLabel}</div>
                      {row._data.image && (
                        <BubbleButton
                          variant="navy"
                          inverse
                          className="p-1-i"
                          linkProps={{
                            // FIXME: not accurate all the time!
                            to: `/uploads/feeds/${row._data.image}`,
                            target: '_blank',
                          }}
                        >
                          Click to Preview
                        </BubbleButton>
                      )}
                    </div>
                  ),
                },
                {
                  key: 'selectedSrc',
                  render: row => (
                    <div>
                      <BubbleSelect
                        value={props.transferForm.bgImagesDict[row.pageId]}
                        options={sourcePageOptions}
                        allowUndefined={false}
                        block
                        onValue={val => {
                          const copy = { ...props.transferForm.bgImagesDict };
                          if (val) copy[row.pageId] = val;
                          changeTransferForm('bgImagesDict', copy);
                        }}
                      />
                      {props.transferForm.bgImagesDict[row.pageId] && (
                        <BubbleButton
                          variant="navy"
                          inverse
                          className="p-1-i"
                          linkProps={{
                            to: `/uploads/feeds/${props.transferForm.bgImagesDict[row.pageId]!}`,
                            target: '_blank',
                          }}
                        >
                          Click to Preview
                        </BubbleButton>
                      )}
                    </div>
                  ),
                },
              ];

              return (
                <div key={feed._id} className="mb-10">
                  <h3 className="text-md text-slate-dark font-semibold mb-2">
                    {feed.name || `Feed ${feedIndex + 1}`}
                  </h3>
                  <BubbleTable
                    headVariant="navy"
                    striped
                    responsive
                    items={tableItems}
                    fields={tableFields}
                    templates={tableTemplates}
                    keyExtractor={row => row.pageId}
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <div>Loading...</div>
        )}
      </FormGenericContainer>
    </div>
  );
}
